import axios from 'axios';
import { auth } from '../config/firebase';
import { reportError } from './errorReporting';
import { signOut } from 'firebase/auth';
import { refreshTokenIfNeeded, setupTokenRefresh } from './tokenRefresh';

// Default configuration
const DEFAULT_CONFIG = {
  // For Hetzner deployment with Nginx, baseURL should match the proxy configuration
  // The frontend is at /affiliate/ and API is at /api/
  baseURL: '/api',
  timeout: 30000, // 30 seconds
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    // Add Origin header to help with CORS
    'Origin': window.location.origin
  },
};

// Log the configured baseURL for debugging
console.log('🔍 API configured with baseURL:', DEFAULT_CONFIG.baseURL);
console.log('🔍 Current location:', window.location.href);

// Create an axios instance with default config
const api = axios.create(DEFAULT_CONFIG);

// Response status codes that should trigger a retry
const RETRY_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// Maximum number of retries for failed requests
const MAX_RETRIES = 3;

/**
 * Handle token refresh
 */
const handleTokenRefresh = async () => {
  try {
    // Use the improved token refresh mechanism
    const token = await refreshTokenIfNeeded();
    
    if (!token) {
      throw new Error('No authenticated user or token refresh failed');
    }
    
    // Return the fresh token
    return token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Sign out user if token refresh fails
    await signOut(auth);
    throw error;
  }
};

// Initialize token refresh mechanisms
setupTokenRefresh();

/**
 * Add authentication token to requests
 */
api.interceptors.request.use(
  async (config) => {
    // Skip token for public routes or auth routes that don't need authentication
    if (config.url.includes('/auth/') && !config.url.includes('/auth/me')) {
      return config;
    }

    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        // Use our refresh token mechanism to get a fresh token if needed
        const token = await refreshTokenIfNeeded();
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // If no token available but we expected one, redirect to login
          // This handles cases where the token is completely invalid or missing
          if (config.requiresAuth !== false && !window.location.pathname.endsWith('/login')) {
            console.warn('Auth token missing for authenticated route');
          }
        }
      }
      return config;
    } catch (error) {
      console.error('Error setting auth token:', error);
      return Promise.reject(error);
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Handle response errors and retries
 */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors
    if (!error.response) {
      reportError(error, { type: 'network_error' });
      return Promise.reject({
        message: 'Network Error',
        details: 'Unable to connect to the server. Please check your internet connection.',
        isNetworkError: true
      });
    }

    const { status, data } = error.response;
    const errorMessage = data?.message || 'An unexpected error occurred';
    
    // Handle 401 Unauthorized (token expired)
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const newToken = await handleTokenRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Redirect to login if token refresh fails
        const loginPath = process.env.PUBLIC_URL ? process.env.PUBLIC_URL + '/login' : '/login';
        if (!window.location.pathname.endsWith('/login')) {
          window.location.href = loginPath + '?sessionExpired=true';
        }
        return Promise.reject({
          message: 'Session expired',
          details: 'Your session has expired. Please log in again.',
          isAuthError: true
        });
      }
    }

    // Handle rate limiting (429)
    if (status === 429) {
      const retryAfter = error.response.headers['retry-after'] || 5;
      return new Promise(resolve => {
        setTimeout(() => {
          resolve(api(originalRequest));
        }, retryAfter * 1000);
      });
    }

    // Handle server errors with retry
    if (RETRY_STATUS_CODES.includes(status) && !originalRequest._retryCount) {
      const retryCount = originalRequest._retryCount || 0;
      if (retryCount < MAX_RETRIES) {
        originalRequest._retryCount = retryCount + 1;
        const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff
        
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(api(originalRequest));
          }, delay);
        });
      }
    }

    // Report the error
    reportError(error, {
      type: 'api_error',
      status,
      url: originalRequest.url,
      method: originalRequest.method,
      response: data
    });

    // Return a standardized error object
    return Promise.reject({
      message: errorMessage,
      status,
      data: data?.data,
      code: data?.code,
      isApiError: true,
      originalError: error
    });
  }
);

// Links API
export const linksApi = {
  getLinks: (params) => api.get('/links', { params }),
  createLink: (data) => api.post('/links', data),
  getLink: (id) => api.get(`/links/${id}`),
  updateLink: (id, data) => api.put(`/links/${id}`, data),
  deleteLink: (id) => api.delete(`/links/${id}`),
  searchLinks: (query) => api.get('/links', { params: { search: query } })
};

// Dashboard API
export const dashboardApi = {
  getStats: (period = '30d') => api.get('/dashboard/stats', { params: { period } }),
  getChartData: (period = '30d', metric = 'clicks') => api.get('/dashboard/chart', { params: { period, metric } }),
  getTopLinks: (limit = 5, metric = 'clicks') => 
    api.get('/dashboard/top-links', { params: { limit, metric } })
};

// Conversions API
export const conversionsApi = {
  getConversions: (params) => api.get('/conversions', { params }),
  getConversionStats: (linkId) => api.get(`/conversions/stats/${linkId}`),
  getRecentConversions: (limit = 10) => api.get('/conversions/recent', { params: { limit } }),
  trackConversion: (trackingId, data) => api.post(`/conversions/track/${trackingId}`, data),
  getConversionChartData: (days = 30) => api.get('/conversions/chart', { params: { days } })
};

// Upgrade/Subscription APIs
export const upgradeApi = {
  // Create a checkout session for Pro subscription
  upgradeToPro: (planType = 'monthly') => api.post('/upgrade', { plan: planType }),
};

export const subscriptionApi = {
  // Get checkout session details by ID
  getCheckoutSession: (sessionId) => api.get(`/subscription/session/${sessionId}`),
  
  // Get current subscription status
  getSubscriptionStatus: () => api.get('/subscription/status'),
  
  // Create customer portal session
  createCustomerPortalSession: () => api.post('/subscription/customer-portal'),
  
  // Cancel subscription
  cancelSubscription: () => api.post('/subscription/cancel')
};

// Export API
export const exportApi = {
  exportLinks: (format = 'csv') => api.get('/export/links', { 
    params: { format },
    responseType: 'blob'
  }),
  exportClicks: (format = 'csv', params) => api.get('/export/clicks', { 
    params: { ...params, format },
    responseType: 'blob'
  }),
  exportConversions: (format = 'csv', params) => api.get('/export/conversions', { 
    params: { ...params, format },
    responseType: 'blob'
  })
};

export default api;
